import Debug from 'debug'
import type { Metadata } from 'grpc-web'

import type { GetPathResponse } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import {
  GetPathRequest,
  ItemType,
} from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import type { PathItemObject } from '@jotta/types/Files'
import type { FileRouteContext } from '@jotta/types/schemas/FileRouteSchema'

import type { SortDir } from '../fileService'
import { getFileServiceClient } from '../fileService'
import { getTranslatedFilenameFromPathItem } from '../getTranslatedFilenameFromApiPath'
import { getAuthMeta } from '../grpcutils'

import { parsePathItem } from './parsePathItem'

export type GetPathItemSortDir = Exclude<SortDir, 'UNKNOWN'>

const debug = Debug('jotta:grpc:file:getPathItem')

export interface GetPathParams {
  context?: FileRouteContext
  path: string
  includeChildren?: boolean
  includeDeleted?: boolean
  includeDownloadLinks?: boolean
  includeThumb?: boolean
  sort?: GetPathItemSortDir
  token?: string
  cursor?: string
  limit?: number
}
export type GetPathOptions = Required<Omit<GetPathParams, 'token'>> & {
  token?: string
}

export interface PathItemObjectResponse {
  cursor: string
  item: PathItemObject
  isFile: boolean
  isFolder: boolean
  options: GetPathOptions
}

/**
 * Set defaults for all undefined GetPathParams
 * @param params Optional params
 * @returns Params with defaults set
 */
export function parseGetPathParams(params: GetPathParams): GetPathOptions {
  const {
    context = 'sync',
    path,
    sort = 'NAME_ASC',
    includeChildren = true,
    includeDeleted = false,
    includeDownloadLinks = false,
    includeThumb = false,
    token,
    limit = includeChildren ? 100 : 1,
    cursor = '',
  } = params
  return {
    context,
    path,
    sort,
    includeDeleted,
    includeChildren,
    includeDownloadLinks,
    includeThumb,
    token,
    limit,
    cursor,
  }
}

/**
 * The token to sign download links with: the one the requests were sent
 * with, or, when the server rejected it and a request was replayed, the
 * renewed one (the middleware records it on that request's metadata). A
 * rejected token is no good for any of the links, and the renewed one is for
 * the same user.
 */
function downloadTokenFor(sent: string, metas: Metadata[]) {
  const renewed = metas.find(m => m.Authorization !== sent)
  return (renewed?.Authorization ?? sent).replace(/^Bearer /, '')
}

export async function baseGetPath(params: GetPathParams) {
  const o = parseGetPathParams(params)
  const meta = await getAuthMeta(o.token)
  const sorting = GetPathRequest.Sort[o.sort]
  const getPathRequest = new GetPathRequest()
  getPathRequest.setPath(o.path)
  getPathRequest.setSort(sorting)
  getPathRequest.setIncludeChildren(o.includeChildren)
  getPathRequest.setIncludeDeleted(o.includeDeleted)
  getPathRequest.setIncludeThumb(o.includeThumb)
  getPathRequest.setLimit(o.limit)
  if (o.cursor) {
    getPathRequest.setCursor(o.cursor)
  }
  const response = await getFileServiceClient().getPath(getPathRequest, meta)
  const cursor = response.getCursor()
  const pathItem = response.getPathItem()

  if (!pathItem) {
    throw new Error('pathItem missing from getPath response')
  }
  return {
    options: o,
    cursor,
    pathItem,
  }
}
export function getPath(
  params: GetPathParams,
): Promise<PathItemObjectResponse> {
  if (params.path.startsWith('/search')) {
    // Ugly hack to compensate for invalid FileStore state triggering invalid fetches.
    // Both FileStore and this code (using old grpc client) should hopefully disappear soon!
    return new Promise(() => {})
  }
  return getPathInternal(params)
}

async function getPathInternal(
  params: GetPathParams,
): Promise<PathItemObjectResponse> {
  const o = parseGetPathParams(params)
  const meta = await getAuthMeta(o.token)
  const sentAuthorization = meta.Authorization
  const sorting = GetPathRequest.Sort[o.sort]
  const getPathRequest = new GetPathRequest()
  getPathRequest.setPath(o.path)
  getPathRequest.setSort(sorting)
  getPathRequest.setIncludeChildren(o.includeChildren)
  getPathRequest.setIncludeDeleted(o.includeDeleted)
  getPathRequest.setIncludeThumb(o.includeThumb)
  getPathRequest.setLimit(o.limit)
  if (o.cursor) {
    getPathRequest.setCursor(o.cursor)
  }
  const getPathRequestPromise = getFileServiceClient().getPath(
    getPathRequest,
    meta,
  )
  const fetchBackupChildren = Boolean(
    o.path === '/backup' && o.includeChildren && !o.cursor,
  )

  const requestPromises: [
    mainRequestPromise: Promise<GetPathResponse>,
    ...additionalChildrenRequestPromises: Promise<GetPathResponse>[],
  ] = [getPathRequestPromise]
  const additionalMetas: Metadata[] = []
  if (fetchBackupChildren) {
    const contactsRequest = new GetPathRequest()
    contactsRequest.setPath('/contacts')
    contactsRequest.setSort(sorting)
    contactsRequest.setIncludeChildren(false)
    contactsRequest.setIncludeDeleted(o.includeDeleted)
    contactsRequest.setIncludeThumb(o.includeThumb)
    contactsRequest.setLimit(1)
    const contactsMeta = { ...meta }
    additionalMetas.push(contactsMeta)
    requestPromises.push(
      getFileServiceClient().getPath(contactsRequest, contactsMeta),
    )
    const photosRequest = new GetPathRequest()
    photosRequest.setPath('/photos')
    photosRequest.setSort(sorting)
    photosRequest.setIncludeChildren(false)
    photosRequest.setIncludeDeleted(o.includeDeleted)
    photosRequest.setIncludeThumb(o.includeThumb)
    photosRequest.setLimit(1)
    const photosMeta = { ...meta }
    additionalMetas.push(photosMeta)
    requestPromises.push(
      getFileServiceClient().getPath(photosRequest, photosMeta),
    )
  }
  const [response, ...additionalChildrenResponses] =
    await Promise.all(requestPromises)
  const cursor = response.getCursor()
  const pathItem = response.getPathItem()

  if (!pathItem) {
    throw new Error('pathItem missing from getPath response')
  }
  const additionalChildren = additionalChildrenResponses.length
    ? additionalChildrenResponses.map(res => {
        const pathItem = res.getPathItem()
        if (!pathItem) {
          throw new Error('pathItem missing from getPath response')
        }
        pathItem.setName(getTranslatedFilenameFromPathItem(pathItem.toObject()))
        return pathItem
      })
    : undefined

  const item = parsePathItem({
    pathItem,
    token: o.token,
    downloadToken: downloadTokenFor(sentAuthorization, [
      meta,
      ...additionalMetas,
    ]),
    additionalChildren,
    ignoreDownloadLinks: !o.includeDownloadLinks,
    ignoreChildren: !o.includeChildren,
    responseCursor: cursor,
  })

  const isFile = item.type === ItemType.FILE
  return {
    cursor,
    options: o,
    item,
    isFile,
    isFolder: !isFile,
  }
}
